import { NATIVE_EMBED_EXTENSION_KEY } from '@atlaskit/editor-common/extensions';
import type { AdditionalNodeContextFragment } from '@atlaskit/editor-common/types/selection';
import type { JSONNode } from '@atlaskit/editor-json-transformer/types';
import type { Node } from '@atlaskit/editor-prosemirror/model';
import type { NodeContextFragmentExtractor } from '@atlassian/editor-plugin-ai-selection-context/editor-actions';
import { getNativeEmbedFacade } from '@atlassian/native-embeds-core/services/main';
import type { ReadContentCommandResult } from '@atlassian/rovo-content-bridge-api-commands/commands/read-content-command';

/**
 * We are limiting context extraction to this temporary allow list of content types at this stage
 * of the object level remix feature development (object level remix is currently only enabled for pages)
 */
const NATIVE_EMBED_CONTENT_TYPE_ALLOW_LIST = ['page'];

export type NativeContentType = ReadContentCommandResult['contentType'];
export interface NativeContentContextFragment extends AdditionalNodeContextFragment {
	contentResult: Promise<ReadContentCommandResult | undefined>;
	contentType: NativeContentType;
	type: 'nativeContentContext';
}

export const nativeEmbedContextExtractor: NodeContextFragmentExtractor = (node: JSONNode) => {
	if (node.type !== 'extension') {
		return null;
	}
	const pmNode: Node = node as unknown as Node;
	if (!pmNode.attrs) {
		return null;
	}
	const localId = pmNode.attrs?.localId;
	if (!localId) {
		return null;
	}
	const extensionKey = pmNode.attrs.extensionKey;
	if (!extensionKey || !extensionKey.startsWith(NATIVE_EMBED_EXTENSION_KEY)) {
		/** return null if not a native embed */
		return null;
	}
	const contentType = extensionKey.split(':')[1];
	if (!NATIVE_EMBED_CONTENT_TYPE_ALLOW_LIST.includes(contentType)) {
		/** return null if the content type is not in the allow list */
		return null;
	}
	const readContentCommandResult = getNativeEmbedFacade().sendReadContent(localId);
	void readContentCommandResult.catch(() => undefined);
	return {
		type: 'nativeContentContext',
		contentType: contentType,
		contentResult: readContentCommandResult,
	};
};
